import React from 'react';
import api from '../../services/api';
import './customFieldValues.css';

// Renders the custom fields defined for an entity, and lets them be filled in.
//
// Without this the admin page is configuration nobody can use: Trey can define a
// field, but no one can put a value in it.
//
// Renders NOTHING when no custom fields are defined, which is the state every
// install starts in — an empty "Custom Fields" heading on every job would be
// worse than no feature at all.
//
// entity: 'Project' | 'Contact'   entityId: the record's id
function CustomFieldValues({ entity, entityId, editable = true }) {
  const [rows, setRows] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [editingId, setEditingId] = React.useState(null);
  const [draft, setDraft] = React.useState('');
  const [savingId, setSavingId] = React.useState(null);
  const [error, setError] = React.useState('');

  const load = React.useCallback(() => {
    if (!entityId) return;
    setLoading(true);
    api.get(`/custom-fields/values/${entity}/${entityId}`)
      // Hidden fields are dropped here rather than server-side: that endpoint is also
      // an integration's read path, and hiding gcUuid from Ground Control would defeat
      // the point of the field.
      .then((res) => setRows((res.data || []).filter(
        (r) => (r.definition?.visibility || 'Detail') !== 'Hidden'
      )))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [entity, entityId]);

  React.useEffect(() => { load(); }, [load]);

  const commit = async (row, value) => {
    const current = row.value ?? '';
    if (String(value ?? '') === String(current)) { setEditingId(null); return; }

    setSavingId(row.definition.id);
    setError('');
    try {
      // The endpoint takes a list and returns the whole set back, so one save
      // cannot leave the panel showing something different from what is stored.
      const res = await api.put(`/custom-fields/values/${entity}/${entityId}`, [
        { definitionId: row.definition.id, value: String(value ?? '') },
      ]);
      setRows(res.data || []);
      setEditingId(null);
    } catch {
      setError('Could not save that field.');
      setEditingId(null);
    } finally {
      setSavingId(null);
    }
  };

  if (loading || rows.length === 0) return null;

  // Group by the section given on the admin page. Null lands in "Custom Fields".
  const groups = rows.reduce((acc, row) => {
    const key = row.definition.section || 'Custom Fields';
    (acc[key] = acc[key] || []).push(row);
    return acc;
  }, {});

  const renderValue = (row) => {
    const def = row.definition;
    const isSaving = savingId === def.id;

    if (isSaving) {
      return <span className="drawer-field-value"><i className="fas fa-spinner fa-spin"></i></span>;
    }

    // Checkbox commits straight from the control — there is nothing to type, so
    // making someone click into an edit mode first is pure friction.
    if (def.type === 'Checkbox') {
      const on = row.value === 'true';
      return (
        <span className="drawer-field-value">
          <input
            type="checkbox"
            checked={on}
            disabled={!editable}
            onChange={(e) => commit(row, e.target.checked ? 'true' : 'false')}
          />
        </span>
      );
    }

    if (editingId === def.id) {
      const common = {
        autoFocus: true,
        className: 'drawer-field-input',
        value: draft,
        onChange: (e) => setDraft(e.target.value),
        onBlur: (e) => commit(row, e.target.value),
        onKeyDown: (e) => {
          if (e.key === 'Escape') setEditingId(null);
          if (e.key === 'Enter' && def.type !== 'TextArea') commit(row, e.target.value);
        },
      };

      if (def.type === 'Select') {
        return (
          <span className="drawer-field-value">
            <select
              {...common}
              onChange={(e) => commit(row, e.target.value)}
            >
              <option value="">— none —</option>
              {(def.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
          </span>
        );
      }
      if (def.type === 'TextArea') {
        return <span className="drawer-field-value"><textarea rows={3} {...common} /></span>;
      }
      return (
        <span className="drawer-field-value">
          <input
            type={def.type === 'Date' ? 'date' : def.type === 'Number' ? 'number' : 'text'}
            step={def.type === 'Number' ? 'any' : undefined}
            {...common}
          />
        </span>
      );
    }

    const shown = row.value || null;
    return (
      <button
        type="button"
        className={`drawer-field-value drawer-field-editable${!shown ? ' empty' : ''}`}
        disabled={!editable}
        onClick={() => { setDraft(row.value ?? ''); setEditingId(def.id); }}
        title={editable ? `Edit ${def.label.toLowerCase()}` : undefined}
      >
        {shown || '—'}
        {editable && <i className="fas fa-pen drawer-field-pen"></i>}
      </button>
    );
  };

  return (
    <>
      {error && <div className="cfv-error">{error}</div>}
      {Object.entries(groups).map(([section, items]) => (
        <div className="drawer-section" key={section}>
          <h4 className="drawer-section-title">{section}</h4>
          {items.map((row) => (
            <div className="drawer-field" key={row.definition.id}>
              <span className="drawer-field-label" title={row.definition.helpText || undefined}>
                {row.definition.label}
              </span>
              {renderValue(row)}
            </div>
          ))}
        </div>
      ))}
    </>
  );
}

export default CustomFieldValues;
